import React from "react";

type Variant = "primary" | "ghost" | "danger" | "info";
type Size = "sm" | "md" | "lg";

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  fullWidth?: boolean;
};

const variantClass: Record<Variant, string> = {
  primary:
    "bg-primary text-ink border-primary/60 hover:bg-primary/90 shadow-glow",
  ghost:
    "bg-transparent text-white border-white/25 hover:bg-white/10",
  danger:
    "bg-alert-critical text-white border-alert-critical/70 hover:brightness-110",
  info:
    "bg-alert-info text-white border-alert-info/70 hover:brightness-110",
};

const sizeClass: Record<Size, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-5 py-2 text-base",
  lg: "px-7 py-3 text-lg",
};

export default function Button({
  variant = "primary",
  size = "md",
  leadingIcon,
  trailingIcon,
  fullWidth,
  className = "",
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled}
      className={[
        "inline-flex items-center justify-center gap-2",
        "rounded-chip border font-medium",
        "transition-transform duration-75 active:scale-[0.97]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
        "disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100",
        variantClass[variant],
        sizeClass[size],
        fullWidth ? "w-full" : "",
        className,
      ].join(" ")}
    >
      {leadingIcon ? <span className="inline-flex">{leadingIcon}</span> : null}
      {children}
      {trailingIcon ? <span className="inline-flex">{trailingIcon}</span> : null}
    </button>
  );
}
